import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Papa from "papaparse";
import { useParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { withStyles } from "@material-ui/core/styles";

import {
  Grid,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Input,
  InputLabel,
  CircularProgress,
} from "@material-ui/core";

import { FilledTextField } from "../../components/FormElements";
import { HibernationSteps, HibernationTypes } from "../../constants";

const styles = (theme) => ({
  stepText: { marginBottom: theme.spacing(1) },
  importLabel: {
    marginTop: theme.spacing(4),
    textAlign: "center",
  },
  importButton: { width: "65%" },
  fileName: {
    marginTop: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  summaryList: {
    paddingLeft: theme.spacing(3),
    marginTop: theme.spacing(0.5),
  },
  confirmInput: {
    "& input": { paddingTop: theme.spacing(2) },
  },
  error: {
    marginTop: theme.spacing(2),
  },
  cancelButton: {
    boxShadow: "none",
    backgroundColor: "transparent",
    "&:hover": {
      boxShadow: "none",
      backgroundColor: theme.palette.background.overlay,
    },
  },
  focused: {}, // material ui sucks, so this empty object is 100% necessary
});

export const HibernateVehiclesModal = (props) => {
  const {
    t,
    activeFleet,
    modalOpen,
    handleModalClose,
    hibernateVehicles,
    hibernateVehiclesDetails,
    setLoading,
    classes,
  } = props;
  const { fleetId } = useParams();

  const [step, setStep] = useState(HibernationSteps.selectType);
  const [type, setType] = useState(HibernationTypes.partial);
  const [file, setFile] = useState(null);
  const [loading, setModalLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedVehicles, setSelectedVehicles] = useState(null);
  const [summaryData, setSummaryData] = useState({});
  const [verificationStr, setVerificationStr] = useState("");
  const [verified, setVerified] = useState(false);

  const reader = new FileReader();

  useEffect(() => {
    if (!modalOpen) {
      setStep(HibernationSteps.selectType);
      setType(HibernationTypes.partial);
      setFile(null);
      setModalLoading(false);
      setError("");
      setSelectedVehicles(null);
      setVerificationStr("");
      setVerified(false);
    }
  }, [modalOpen]);

  useEffect(() => {
    setVerified(
      type === HibernationTypes.full && verificationStr === `Fully hibernate ${activeFleet.name}` ||
      type === HibernationTypes.partial && verificationStr === `Partially hibernate ${activeFleet.name}`,
    );
  }, [verificationStr]);

  useEffect(() => {
    async function getDetails() {
      const result = await hibernateVehiclesDetails(fleetId, selectedVehicles);

      if (result.error) {
        setError(t("Ota.Hibernation.Errors.HibernationDetails"));
      } else {
        setSummaryData({
          count: result.vehicle_count,
          eligible: result.eligible_vehicles,
          noShortcode: result.no_shortcode,
          notWarehouse: result.not_in_warehouse,
          notWarehouseReserved: result.not_in_warehouse_and_reserved,
          offline: result.offline,
          reserved: result.reserved_vehicles,
        });
        setStep(HibernationSteps.summarizeCsv);
      }
      setModalLoading(false);
    }

    if (selectedVehicles) {
      setModalLoading(true);
      getDetails();
    }
  }, [selectedVehicles]);

  const onChangeType = (event) => {
    setType(event.target.value);
  };

  const onFileChange = (event) => {
    const { files } = event.target;

    if (files.length) {
      setFile(files[0]);
      setError("");
    }
  };

  const onChangeVerification = (event) => {
    setVerificationStr(event.target.value);
  };

  const readCsvFile = async () => {
    if (!file) return;
    try {
      setModalLoading(true);
      reader.onload = (event) => {
        const fileContent = event.target.result;

        const results = Papa.parse(fileContent, { header: true });
        const fields = ["shortcode", "Shortcode"];
        const headers = results.meta.fields;

        if (!headers || !headers.some((h) => fields.includes(h))) {
          const errorMessage = `${t("Ota.Hibernation.Errors.Upload")}: ${
            t("Ota.Hibernation.Errors.MissingHeader")}`;
          setError(errorMessage);
          setModalLoading(false);
          return;
        }

        const rows = results.data;
        const shortcodes = rows
          .map((vehicle) => vehicle.shortcode || vehicle.Shortcode)
          .filter((vehicle) => vehicle);

        if (!shortcodes.length) {
          const errorMessage = `${t("Ota.Hibernation.Errors.Upload")}: ${
            t("Ota.Hibernation.Errors.MissingShortcodes")}`;
          setError(errorMessage);
          setModalLoading(false);
          return;
        }
        setSelectedVehicles(shortcodes);
      };
      reader.readAsText(file);
    } catch (err) {
      setError(`${t("Ota.Hibernation.Errors.Upload")}: ${err.message || err}`);
      setModalLoading(false);
    }
  };

  const onBack = () => {
    switch (step) {
      case HibernationSteps.selectType:
        handleModalClose();
        break;
      case HibernationSteps.uploadCsv:
        setError("");
        setStep(HibernationSteps.selectType);
        break;
      case HibernationSteps.summarizeCsv:
        setSelectedVehicles(null);
        setStep(
          type === HibernationTypes.full
            ? HibernationSteps.selectType
            : HibernationSteps.uploadCsv,
        );
        break;
      case HibernationSteps.confirm:
        setVerified(false);
        setVerificationStr("");
        setStep(HibernationSteps.summarizeCsv);
        break;
      default:
        break;
    }
  };

  const onNext = async () => {
    switch (step) {
      case HibernationSteps.selectType:
        if (type === HibernationTypes.partial) {
          setStep(HibernationSteps.uploadCsv);
        } else {
          setSelectedVehicles([]);
        }
        break;
      case HibernationSteps.uploadCsv:
        if (file) {
          await readCsvFile();
        }
        break;
      case HibernationSteps.summarizeCsv:
        setStep(HibernationSteps.confirm);
        break;
      case HibernationSteps.confirm:
        if (verified) {
          handleModalClose();
          setLoading(true);
          await hibernateVehicles(fleetId, selectedVehicles);
          setLoading(false);
        }
        break;
      default:
        break;
    }
  };

  const nextDisabled = loading ||
    step === HibernationSteps.uploadCsv && (!file || !!error) ||
    step === HibernationSteps.summarizeCsv && !summaryData?.eligible ||
    step === HibernationSteps.confirm && !verified;

  return (
    <Dialog
      open={modalOpen}
      onClose={handleModalClose}
      aria-labelledby="hibernate-vehicles-modal"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        {`${t("Ota.Hibernation.Title")} ${step >= HibernationSteps.summarizeCsv
          ? `${summaryData.eligible} ${t("Ota.Hibernation.Scooters")} ` : ""}
          ${activeFleet.name}?`}
      </DialogTitle>
      <DialogContent>
        {step === HibernationSteps.selectType && (
          <Grid>
            <Typography className={classes.stepText}>
              {t("Ota.Hibernation.Steps.SelectType")}
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                row
                name="type"
                value={type}
                onChange={onChangeType}
              >
                <FormControlLabel
                  value={HibernationTypes.partial}
                  control={<Radio />}
                  label={t(`Ota.Hibernation.${HibernationTypes.partial}`)}
                />
                <FormControlLabel
                  value={HibernationTypes.full}
                  control={<Radio />}
                  label={t(`Ota.Hibernation.${HibernationTypes.full}`)}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        )}
        {step === HibernationSteps.uploadCsv && (
          <Grid>
            <Typography className={classes.stepText}>
              {t("Ota.Hibernation.Steps.UploadCsv")}
            </Typography>
            <Grid>
              <Input
                type="file"
                id="uploadCsv"
                inputProps={{ accept: ".csv" }}
                onChange={onFileChange}
                style={{ display: "none" }}
              />
              <InputLabel htmlFor="uploadCsv" className={classes.importLabel}>
                <Button
                  className={classes.importButton}
                  color="primary"
                  variant="contained"
                  component="div"
                >
                  {t("Ota.Hibernation.Import")}
                </Button>
              </InputLabel>
            </Grid>
            <Typography className={classes.fileName}>
              {file
                ? t("Ota.Hibernation.File")
                : t("Ota.Hibernation.NoFile")}
              {!!file && `: ${file.name}`}
            </Typography>
          </Grid>
        )}
        {step === HibernationSteps.summarizeCsv && (
          <Grid>
            <Typography>
              {type === HibernationTypes.full ? activeFleet.name
                : t("Ota.Hibernation.Steps.Summarize.CSV")} {
                t("Ota.Hibernation.Steps.Summarize.Title1")} {summaryData.count} {
                t("Ota.Hibernation.Steps.Summarize.Title2")}:
            </Typography>
            <ul className={classes.summaryList}>
              <li>{summaryData.eligible} – {t("Ota.Hibernation.Steps.Summarize.Eligible")}</li>
              <li>
                {summaryData.count - summaryData.eligible} – {t("Ota.Hibernation.Steps.Summarize.NotEligible")}
                <ul>
                  {!!summaryData.notWarehouse && (
                    <li>{summaryData.notWarehouse} – {t("Ota.Hibernation.Steps.Summarize.NotWarehouse")}</li>
                  )}
                  {!!summaryData.reserved && (
                    <li>{summaryData.reserved} – {t("Ota.Hibernation.Steps.Summarize.Reserved")}</li>
                  )}
                  {!!summaryData.notWarehouseReserved && (
                    <li>{summaryData.notWarehouseReserved} – {t("Ota.Hibernation.Steps.Summarize.NotWarehouseReserved")}</li>
                  )}
                  {!!summaryData.noShortcode && (
                    <li>{summaryData.noShortcode} – {t("Ota.Hibernation.Steps.Summarize.Invalid")}</li>
                  )}
                  {!!summaryData.offline && (
                    <li>{summaryData.offline} – {t("Ota.Hibernation.Steps.Summarize.Offline")}</li>
                  )}
                </ul>
              </li>
            </ul>
          </Grid>
        )}
        {step === HibernationSteps.confirm && (
          <Grid>
            <Typography className={classes.stepText}>
              {t("Ota.Hibernation.Steps.Confirm.Text1")} {summaryData.eligible} {
                t("Ota.Hibernation.Steps.Confirm.Vehicles")}
            </Typography>
            <Typography className={classes.stepText}>
              {t("Ota.Hibernation.Steps.Confirm.Type")} &quot;{type === HibernationTypes.full
                ? t("Ota.Hibernation.Steps.Confirm.Full")
                : t("Ota.Hibernation.Steps.Confirm.Partial")} {activeFleet.name}&quot; {
                t("Ota.Hibernation.Steps.Confirm.Text2")}
            </Typography>
            <FilledTextField
              value={verificationStr}
              onChange={onChangeVerification}
              underlineColor="#A8ACB7"
              className={classes.confirmInput}
              InputProps={{
                onPaste: (event) => event.preventDefault(),
              }}
            />
          </Grid>
        )}
        {!!error && (
          <Typography color="error" className={classes.error}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          className={step === HibernationSteps.selectType ? classes.cancelButton : ""}
          onClick={onBack}
          color="primary"
          variant="contained"
        >
          {step === HibernationSteps.selectType
            ? t("Ota.Modals.Cancel")
            : t("Ota.Modals.Back")
          }
        </Button>
        <Button
          onClick={onNext}
          color="primary"
          variant="contained"
          disabled={nextDisabled}
        >
          {loading && (
            <CircularProgress color="primary" size={24} />
          )}
          {!loading && (
            step === HibernationSteps.confirm
              ? t("Ota.Modals.Submit")
              : t("Ota.Modals.Next")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

HibernateVehiclesModal.propTypes = {
  t: PropTypes.func.isRequired,
  modalOpen: PropTypes.bool.isRequired,
  handleModalClose: PropTypes.func.isRequired,
  hibernateVehicles: PropTypes.func.isRequired,
  hibernateVehiclesDetails: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  activeFleet: PropTypes.object.isRequired,
};

export default withTranslation("common")(withStyles(styles)(HibernateVehiclesModal));
